import { useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Table, Typography, Button, Tooltip, Empty, Alert, Skeleton } from 'antd';
import { motion, AnimatePresence } from 'motion/react';
import { useSearchWorkflowsInfiniteQuery } from '../../store/workflows/workflowApi';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { removeUnderscoresAndCapitalize } from '@admin-panel/utils';
import StatusActions from './StatusActions';
import ViewProductsDrawer from './ViewProductsDrawer';
import { WorkflowDTOType } from '../../schemas';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  clearWorkflowCreatePayload,
  selectWorkflowFilters,
  setIsDrawerOpen,
  setWorkflowFilters,
  updateWorkflowCreatePayload,
} from '../../store/workflows/slice';
import Discovery from './Discovery';
import UpsertTaggingWorkflowDrawer from '../Products/UpsertTaggingWorkflowDrawer';
import { clearAllFilters, setFilter } from '../../store/products/slice';
import useSyncWithUrl from './ViewProductsDrawer/useSyncWithUrl';
import * as Styles from './styles';

const getColumns = ({
  openProductsDrawer,
  openEditDrawer,
}: {
  openProductsDrawer: (workflow: WorkflowDTOType) => void;
  openEditDrawer: (workflow: WorkflowDTOType) => void;
}) => {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Tooltip title={name}>
          <span
            style={{
              display: 'inline-block',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            }}
          >
            {name}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (type ? removeUnderscoresAndCapitalize(type) : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_: any, record: any) => (
        <StatusActions status={record.status || 'RUNNING'} id={record.id} />
      ),
    },
    {
      title: 'Schedule Type',
      dataIndex: 'scheduleType',
      key: 'scheduleType',
      render: (type: string) => (type ? removeUnderscoresAndCapitalize(type) : '-'),
    },
    {
      title: 'Schedule Start Time',
      dataIndex: 'scheduleStartTime',
      key: 'scheduleStartTime',
      render: (date?: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: 'Schedule End Time',
      dataIndex: 'scheduleEndTime',
      key: 'scheduleEndTime',
      render: (date?: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: 'Description Needed',
      dataIndex: 'descriptionNeeded',
      key: 'descriptionNeeded',
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    {
      title: 'Products Pending QC',
      key: 'id',
      render: (_: any, record: WorkflowDTOType) => (
        <Button type="link" onClick={() => openProductsDrawer(record)}>
          View Products
        </Button>
      ),
    },
    {
      title: 'Edit',
      key: 'id',
      render: (_: any, record: WorkflowDTOType) => (
        <Button type="link" onClick={() => openEditDrawer(record)}>
          Edit
        </Button>
      ),
    },
  ];
};

const TableSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          gap: '16px',
          padding: '16px 16px',
          borderBottom: '1px solid #f0f0f0',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <div style={{ flex: '0 0 180px' }}>
          <Skeleton.Input size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <Skeleton.Input size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 100px' }}>
          <Skeleton.Button size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 140px' }}>
          <Skeleton.Input size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <Skeleton.Input size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <Skeleton.Input size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <Skeleton.Input size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 140px' }}>
          <Skeleton.Button size="small" active style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '0 0 80px' }}>
          <Skeleton.Button size="small" active style={{ width: '100%' }} />
        </div>
      </div>
    ))}
  </motion.div>
);

const Workflows = () => {
  const filters = useAppSelector(selectWorkflowFilters);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } =
    useSearchWorkflowsInfiniteQuery({ filters });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDTOType | null>(null);

  const dispatch = useAppDispatch();

  const workflows = data?.pages.flat() || [];

  const openProductsDrawer = useCallback(
    (workflow: WorkflowDTOType) => {
      setSelectedWorkflow(workflow);
      setDrawerOpen(true);

      navigate(`/tagging-jobs?productsDrawer=${workflow.id}`, {
        replace: false,
      });
    },
    [navigate],
  );

  const openEditDrawer = useCallback(
    (workflow: WorkflowDTOType) => {
      dispatch(clearAllFilters());
      const filters = workflow.scopeQuerySet?.[0]?.filters;
      if (filters && Array.isArray(filters)) {
        filters.forEach(f => {
          dispatch(
            setFilter({
              field: f.field,
              values: f.value.split(','),
              filterType: f.filterType,
            }),
          );
        });
      }
      dispatch(updateWorkflowCreatePayload(workflow));
      dispatch(setIsDrawerOpen(true));
    },
    [dispatch],
  );

  useSyncWithUrl({
    openProductsDrawer,
    workflows: workflows.filter(Boolean),
  });

  const handleCreateWorkflow = () => {
    dispatch(clearAllFilters());
    dispatch(clearWorkflowCreatePayload());
    dispatch(setIsDrawerOpen(true));
  };

  const columns = useMemo(() => {
    return getColumns({
      openProductsDrawer: openProductsDrawer,
      openEditDrawer: openEditDrawer,
    });
  }, [openProductsDrawer, openEditDrawer]);

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  return (
    <Styles.Wrapper>
      <Discovery />

      <Styles.Content>
        <Styles.Header>
          <Typography.Paragraph
            strong
            style={{
              fontSize: 16,
              margin: 0,
            }}
          >
            Tagging Jobs
          </Typography.Paragraph>
          <Button
            type="primary"
            onClick={handleCreateWorkflow}
            style={{ marginLeft: 'auto' }}
            disabled={isLoading}
          >
            Create Tagging Job
          </Button>
        </Styles.Header>

        <AnimatePresence mode="wait">
          {isLoading ? (
            <TableSkeleton rows={8} />
          ) : isError ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <Alert
                message="Failed to load tagging jobs"
                description={error?.message || 'An unexpected error occurred'}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                action={
                  <Button size="small" type="primary" onClick={() => window.location.reload()}>
                    Retry
                  </Button>
                }
              />
              <Empty description="Could not find tagging jobs." />
            </motion.div>
          ) : workflows.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <Empty description="No tagging jobs found" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                <Button type="primary" onClick={handleCreateWorkflow}>
                  Create Your First Tagging Job
                </Button>
              </Empty>
            </motion.div>
          ) : (
            <Styles.TableWrapper
              key="table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Table
                dataSource={workflows}
                columns={columns}
                rowKey="id"
                pagination={false}
                scroll={{ x: true }}
                components={{
                  body: {
                    row: (props: any) => {
                      const isLast = props['data-row-key'] === workflows[workflows.length - 1]?.id;
                      return <tr {...props} ref={isLast ? lastItemRef : undefined} />;
                    },
                  },
                }}
              />

              <AnimatePresence>{isFetchingNextPage && <TableSkeleton rows={3} />}</AnimatePresence>
            </Styles.TableWrapper>
          )}
        </AnimatePresence>

        <ViewProductsDrawer
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedWorkflow(null);

            const params = new URLSearchParams(location.search);

            const isFilteredById = filters.some(f => f.field === 'id' && f.filterType === 'EQ');

            if (isFilteredById) {
              dispatch(
                setWorkflowFilters(filters.filter(f => f.field !== 'id' || f.filterType !== 'EQ')),
              );
            }

            params.delete('productsDrawer');

            navigate(location.pathname, {
              replace: false,
            });
          }}
          workflow={selectedWorkflow}
        />
      </Styles.Content>
      <UpsertTaggingWorkflowDrawer />
    </Styles.Wrapper>
  );
};

export default Workflows;
